import { useEffect, useState } from "react";
import { AlertTriangle, KeyRound, Lock } from "lucide-react";
import { auth } from "./lib/api.js";
import * as vault from "./lib/keyvault.js";
import { C, inputStyle } from "./theme.js";

const MIN = 10;

/**
 * החלפת סיסמה.
 *
 * המורכבות היחידה כאן היא ההצפנה: המפתח הפרטי של צילומי תעודות הזהות
 * ארוז בסיסמה. לכן הרצף הוא לפתוח את הכספת בסיסמה הישנה, לארוז מחדש
 * בחדשה, ולשלוח את שתי הפעולות בבקשה אחת. השרת שומר אותן בטרנזקציה —
 * מצב ביניים כאן פירושו אובדן הצילומים.
 */
export default function PasswordCard() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [hasKeys, setHasKeys] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    vault.loadKeys().then(() => setHasKeys(vault.hasKeys())).catch(() => {});
  }, []);

  const set = (k) => (e) => {
    setF({ ...f, [k]: e.target.value });
    setErr(""); setOk("");
  };

  const problem =
    f.next.length < MIN ? `הסיסמה החדשה חייבת להיות באורך ${MIN} תווים לפחות`
      : f.next !== f.confirm ? "האימות אינו תואם"
        : f.next === f.current ? "הסיסמה החדשה זהה לנוכחית"
          : null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (problem) { setErr(problem); return; }

    setBusy(true); setErr(""); setOk("");
    try {
      let byPassword = null;

      if (hasKeys) {
        /* רק כספת ישנה שתלויה בסיסמה בלבד נארזת מחדש. כספת עם עטיפת
           תעודת זהות נפתחת בת"ז — החלפת הסיסמה לא נוגעת בה. */
        const w = await vault.wrappings();
        if (w.password && !w.ownerId) {
          // פתיחה בסיסמה הישנה. אם היא שגויה נעצור כאן, לפני שנגענו בכלום.
          if (!vault.isUnlocked()) await vault.unlock(f.current);
          byPassword = await vault.resealFor(f.next);
        }
      }

      await auth.changePassword(f.current, f.next, byPassword);
      if (byPassword) vault.noteResealed(byPassword);

      setF({ current: "", next: "", confirm: "" });
      setOk("הסיסמה הוחלפה. שאר המכשירים נותקו.");
    } catch (ex) {
      setErr(ex.message || "החלפת הסיסמה נכשלה");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={16} style={{ color: C.teal }} />
        <h2 className="disp text-xl font-bold">סיסמה</h2>
      </div>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        החלפת הסיסמה מנתקת את כל שאר המכשירים. המכשיר הזה יישאר מחובר.
      </p>

      <form onSubmit={submit} className="grid gap-2" style={{ maxWidth: 380 }}>
        <label className="block">
          <span className="text-[12px]" style={{ color: C.muted }}>סיסמה נוכחית</span>
          <input type="password" autoComplete="current-password" dir="ltr"
            value={f.current} onChange={set("current")} style={inputStyle} required />
        </label>

        <label className="block">
          <span className="text-[12px]" style={{ color: C.muted }}>סיסמה חדשה</span>
          <input type="password" autoComplete="new-password" dir="ltr"
            value={f.next} onChange={set("next")} style={inputStyle} required />
        </label>

        <label className="block">
          <span className="text-[12px]" style={{ color: C.muted }}>אימות</span>
          <input type="password" autoComplete="new-password" dir="ltr"
            value={f.confirm} onChange={set("confirm")} style={inputStyle} required />
        </label>

        {hasKeys && (
          <div className="rounded-lg p-2.5 text-[12px] flex gap-2"
            style={{ background: "#F1F4F1", color: C.muted }}>
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>
              מפתח ההצפנה של צילומי תעודות הזהות ייארז מחדש בסיסמה החדשה,
              כדי שהם יישארו קריאים. קוד השחזור לא משתנה.
            </span>
          </div>
        )}

        {err && (
          <div className="rounded-lg p-2.5 text-sm flex items-start gap-2"
            style={{ background: "#FBEDE9", color: "#7A2E1E" }}>
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}
        {ok && (
          <div className="rounded-lg p-2.5 text-sm" style={{ background: "#E6F2EC", color: C.paid }}>
            {ok}
          </div>
        )}

        <button type="submit" disabled={busy}
          className="rounded-xl py-2.5 text-sm mt-1"
          style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
          {busy ? "מחליף…" : "החלפת סיסמה"}
        </button>
      </form>
    </div>
  );
}
